/**
 * One shift on the grid.
 *
 * Draggable, clickable, and it must not be both at once: pragmatic drag and
 * drop does not swallow the click that follows a completed drag the way native
 * HTML5 dragging does, so without the `draggedRef` guard every move would also
 * pop the edit drawer open on the card it just dropped.
 *
 * The browser's own drag image is replaced with one we draw. Not a preference:
 * a native drag image has platform opacity applied that no CSS of ours can
 * reach, so the thing under the cursor would look washed out no matter what we
 * do to `.is-dragging`, and on Windows it degrades further past 280px wide.
 */
import { useEffect, useRef, useState } from 'react';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { Hand, Lock, StickyNote, X } from 'lucide-react';
import type { Shift } from '../lib/api.ts';
import { fmtDuration, fmtMoney, fmtTimeRange, fmtTimeRangeCompact, shiftMinutes } from '../lib/time.ts';
import { colourTokens } from '../components/Bits.tsx';
import { SHIFT_DRAG, type ShiftDrag } from './dnd.ts';
import { drawDragPreview } from './dragPreview.ts';

export function ShiftCard({
  shift,
  colour,
  areaName,
  cost,
  showCost,
  onOpen,
  onDelete,
  draggableCard = true,
}: {
  shift: Shift;
  colour: string | null;
  areaName?: string | null;
  cost: number;
  showCost: boolean;
  onOpen?: (id: string) => void;
  /** Absent when the card is not the boss's to remove. */
  onDelete?: (id: string) => void;
  draggableCard?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const draggedRef = useRef(false);
  const tokens = colourTokens(colour);
  const minutes = shiftMinutes(shift.startTime, shift.endTime, shift.breakMinutes);
  // Nobody on it yet. Drawn dashed in the caution colour so it reads as a gap
  // to fill rather than as a shift like the others.
  const open = !shift.userId;
  // An optimistic card, on screen before the server has answered: it pops in.
  // The real card that replaces it arrives quietly, so a drop pops once.
  const fresh = shift.id.startsWith('pending-');

  useEffect(() => {
    const el = ref.current;
    if (!el || !draggableCard) return;
    const payload: ShiftDrag = {
      type: SHIFT_DRAG,
      shiftId: shift.id,
      fromUserId: shift.userId,
      fromAreaId: shift.areaId,
      fromDate: shift.date,
      locked: shift.locked,
    };
    return draggable({
      element: el,
      getInitialData: () => ({ ...payload }),
      onGenerateDragPreview: ({ nativeSetDragImage }) =>
        setCustomNativeDragPreview({
          nativeSetDragImage,
          // Offset so the card sits beside the cursor rather than under it,
          // which would hide the cell being aimed at.
          getOffset: pointerOutsideOfPreview({ x: '12px', y: '10px' }),
          render: ({ container }) => {
            drawDragPreview(container, {
              title: fmtTimeRange(shift.startTime, shift.endTime),
              subtitle: areaName ?? fmtDuration(minutes),
              colour: tokens,
            });
          },
        }),
      onDragStart: () => {
        setDragging(true);
        draggedRef.current = true;
      },
      onDrop: () => {
        setDragging(false);
        // Cleared on the next tick so the click that may follow a genuine drop
        // is consumed, while a cancelled drag (which produces no click) still
        // resets.
        setTimeout(() => {
          draggedRef.current = false;
        }, 0);
      },
    });
  }, [shift.id, shift.userId, shift.areaId, shift.date, shift.locked, draggableCard, shift.startTime, shift.endTime, areaName, minutes, tokens.soft, tokens.solid]);

  const tip = [
    `${fmtTimeRange(shift.startTime, shift.endTime)}`,
    [
      areaName ? areaName : null,
      `${fmtDuration(minutes)} paid`,
      shift.breakMinutes > 0 ? `${shift.breakMinutes}m unpaid break` : null,
      showCost ? fmtMoney(cost) : null,
      shift.locked ? 'Locked' : shift.status === 'published' ? 'Published' : 'Draft, not yet published',
      shift.needsCover ? `NEEDS COVER${shift.coverReason ? `: ${shift.coverReason}` : ''}` : null,
      shift.notes ? `Note: ${shift.notes}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
  ].join('|');

  return (
    <div
      ref={ref}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      data-tip={tip}
      aria-label={`Shift ${fmtTimeRange(shift.startTime, shift.endTime)}${areaName ? ` in ${areaName}` : ''}`}
      onClick={() => {
        if (draggedRef.current) {
          draggedRef.current = false;
          return;
        }
        onOpen?.(shift.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(shift.id);
        }
      }}
      className={`shift-card group relative w-full select-none px-2 py-1.5 text-left ${dragging ? 'is-dragging' : ''} ${
        fresh ? 'anim-pop' : ''
      } ${onOpen ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={{
        borderRadius: 'var(--sp-radius-sm)',
        background: shift.needsCover
          ? `repeating-linear-gradient(135deg, ${tokens.soft} 0 7px, var(--sp-warn-soft) 7px 14px)`
          : open
            ? 'var(--sp-caution-soft)'
            : tokens.soft,
        border: `1px ${open ? 'dashed' : 'solid'} ${
          shift.needsCover ? 'var(--sp-warn-strong)' : open ? 'var(--sp-caution)' : tokens.solid
        }`,
        // A draft shift is visibly provisional: the dashed left edge is the
        // difference between "the team has seen this" and "you are still
        // thinking about it", and a boss must never confuse the two.
        borderLeft: `3px ${shift.status === 'published' ? 'solid' : 'dashed'} ${open ? 'var(--sp-caution)' : tokens.solid}`,
        boxShadow: 'var(--sp-shadow-1)',
      }}
    >
      {/* Delete without opening the shift first. Hidden until hover on a mouse,
          always present on touch where there is no hover to reveal it, and it
          stops the click from also opening the drawer behind it.

          Deleting is one click, so the safety is the undo that follows rather
          than a confirm dialog in the way of the common case. */}
      {onDelete && !shift.locked ? (
        <button
          type="button"
          className="cell-del"
          title={`Delete this shift|${fmtTimeRange(shift.startTime, shift.endTime)}. You can undo it straight after.`}
          aria-label={`Delete the ${fmtTimeRange(shift.startTime, shift.endTime)} shift`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(shift.id);
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <X size={11} aria-hidden="true" strokeWidth={3} />
        </button>
      ) : null}

      <div className="flex items-center gap-1">
        {/* A phone column is too narrow for "7:30am to 4pm", so the word goes
            rather than the end time. */}
        <span className="tabular min-w-0 flex-1 truncate text-[11.5px] font-bold leading-tight text-ink sm:hidden">
          {fmtTimeRangeCompact(shift.startTime, shift.endTime)}
        </span>
        <span className="tabular hidden min-w-0 flex-1 truncate text-[12.5px] font-bold leading-tight text-ink sm:block">
          {fmtTimeRange(shift.startTime, shift.endTime)}
        </span>
        {shift.notes ? (
          <StickyNote size={11} aria-hidden="true" style={{ color: 'var(--sp-text-faint)' }} />
        ) : null}
        {shift.locked ? (
          <Lock size={11} aria-hidden="true" style={{ color: 'var(--sp-warn-strong)' }} />
        ) : null}
        {/* The person on this shift has said they cannot work it. Without this
            a boss publishes a week containing a shift nobody will turn up for. */}
        {shift.needsCover ? (
          <Hand size={11} aria-hidden="true" style={{ color: 'var(--sp-warn-strong)' }} />
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-1">
        {/* Duration on a phone: a truncated area name ("Mana...") tells the
            boss nothing, and the area is already the row grouping. */}
        <span className="min-w-0 truncate text-[11px] font-medium leading-tight text-muted sm:hidden">
          {fmtDuration(minutes)}
        </span>
        <span className="hidden min-w-0 truncate text-[11px] font-medium leading-tight text-muted sm:block">
          {areaName ?? fmtDuration(minutes)}
        </span>
        {showCost ? (
          <span className="tabular shrink-0 text-[11px] font-bold leading-tight" style={{ color: tokens.solid }}>
            {fmtMoney(cost, { cents: false })}
          </span>
        ) : null}
      </div>
    </div>
  );
}
